import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';

// ==============================|| GLOBAL TABLE - CELL OVERFLOW TOOLTIP ||============================== //
// Generic per-cell wrapper: when the content is ACTUALLY cropped, a tooltip shows the
// text extracted from the cell (see cellTextOf in sorting.ts). Cropping is measured on
// the wrapper box AND on its descendants — components like Chip or a noWrap Typography
// clip INSIDE themselves (ellipsis on an inner span) without ever overflowing the
// wrapper, yet still need the tooltip. Components that bring their own overflow tooltip
// (TruncatedText, TableText) mark their root with `data-self-tooltip`; cropped elements
// inside such a subtree are ignored here, so tooltips never double up.

interface CellOverflowTooltipProps {
	/** Full text shown in the tooltip; null renders the content without a tooltip. */
	title: string | null;
	children: ReactNode;
}

export const CellOverflowTooltip: FC<CellOverflowTooltipProps> = ({
	title,
	children
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const [cropped, setCropped] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		const isCropped = (node: HTMLElement): boolean =>
			node.scrollWidth > node.clientWidth + 1;
		const check = (): void => {
			if (isCropped(el)) {
				setCropped(true);
				return;
			}
			// Trunchierea se întâmplă de regulă pe un element INTERIOR (span-ul de label
			// al unui Chip, un Typography noWrap) — wrapper-ul rămâne la dimensiune fixă.
			const descendants = Array.from(el.querySelectorAll<HTMLElement>('*'));
			setCropped(
				descendants.some(
					(node) => isCropped(node) && !node.closest('[data-self-tooltip]')
				)
			);
		};
		check();
		if (typeof ResizeObserver === 'undefined') return undefined;
		const observer = new ResizeObserver(check);
		observer.observe(el);
		return () => observer.disconnect();
	}, [children]);

	const content = (
		<Box
			ref={ref}
			className="cz-cell-content"
			sx={{
				minWidth: 0,
				maxWidth: '100%',
				overflow: 'hidden',
				display: 'flex',
				alignItems: 'center'
			}}
		>
			{children}
		</Box>
	);

	if (!title) return content;

	// An empty title disables the MUI tooltip, so it only appears while cropped.
	return (
		<Tooltip title={cropped ? title : ''} disableInteractive>
			{content}
		</Tooltip>
	);
};
